import './perks-post-top-comment.component.scss';

import { h } from 'preact';

import { useTranslation } from '../../../../../i18n';
import { useAppSelector } from '../../../../../store/hooks';
import { linkify } from '../../../../../utils/a11y.utils';
import type { Post } from '../../../../model/post.interface';
import { selectUser } from '../../../../store/perks.selectors';
import { PerksAvatar } from '../../../perks-avatar/perks-avatar.component';
import { COMPACT_AVATAR_BADGE_SIZE_PX } from '../../../perks-badge/perks-badge.component';

/** The feed cuts a longer top comment after this many words; the post page shows it in full. */
const TOP_COMMENT_WORD_LIMIT = 40;
// The first TOP_COMMENT_WORD_LIMIT words, keeping the commenter's own spacing between them.
const LEADING_WORDS = new RegExp(`^\\s*(?:\\S+\\s+){${TOP_COMMENT_WORD_LIMIT - 1}}\\S+`);

interface Props {
  post: Post;
  /** focusInput: land on the post page with the comment input focused. */
  goToPost: (focusInput?: boolean) => void;
}

export const PerksPostTopComment = ({ post, goToPost }: Props) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const commentCount = post?.counts?.value?.comments;
  const hasTopComment = !!post?.topComment;
  const hasNoComments = !hasTopComment && (!post?.counts?.value || commentCount === 0);
  const commentText = post?.topComment?.textContent ?? '';
  const leadingWords = commentText.match(LEADING_WORDS)?.[0];
  const isTruncated = !!leadingWords && /\S/.test(commentText.slice(leadingWords.length));

  return (
    <div
      className="single-top-comment"
      {...linkify(() => goToPost(hasNoComments), t('a11y.viewComments'))}
    >
      <div className="single-top-comment-header">
        <h5>{t('perks.comments')}</h5>
        {commentCount !== 0 && <h5 className="comment-count">{commentCount}</h5>}
      </div>

      {hasNoComments && (
        <div className="single-top-comment-body">
          <PerksAvatar user={user} size={30} badgeSize={COMPACT_AVATAR_BADGE_SIZE_PX} />
          <div className="single-comment-input-wrapper">
            <p>{t('perks.addComment')}</p>
          </div>
        </div>
      )}

      {hasTopComment && (
        <div className="single-top-comment-body">
          <PerksAvatar
            user={post.topComment.user}
            size={30}
            badgeSize={COMPACT_AVATAR_BADGE_SIZE_PX}
          />
          <div className="single-comment-input-wrapper">
            <p className="single-comment-text">
              {isTruncated ? `${leadingWords}…` : commentText}
              {isTruncated && (
                <>
                  {' '}
                  {/* The whole card already opens the post's comments; this is just the cue. */}
                  <span className="single-comment-read-more">{t('common.readMore')}</span>
                </>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
